/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { BarRounded } from "@visx/shape";
import { ScaleLinear, ScaleBand } from "d3-scale";
import dayjs from "dayjs";
import { theme } from "@nordcloud/gnui";

const THRESHOLD = 0;

type Props = {
  data: number[];
  benefit: number[];
  yScale: ScaleLinear<number, number>;
  xScale: ScaleBand<number>;
  yMax: number;
  forecast?: number;
  barWidth: number;
};

export function Bars({
  data,
  yScale,
  xScale,
  yMax,
  forecast,
  benefit,
  barWidth,
}: Props) {
  const offset = forecast !== undefined ? 8 : 3;

  const chartBars = data?.map((value, index) => {
    const barHeight = yMax - (yScale(value) ?? 0);
    const barY = yMax - barHeight - 1; // -1 is for the Axis
    const barX = xScale(index) || 0;

    const barFillColor =
      benefit[index] >= THRESHOLD ? theme.colors.success : theme.colors.danger;

    if (forecast !== undefined && index === dayjs().month()) {
      const forecastHeight = yMax - (yScale(forecast) ?? 0);
      const forecastY = yMax - forecastHeight - 1; // -1 is for the Axis
      const barForecastColor =
        benefit[index] >= THRESHOLD
          ? theme.colors.success
          : theme.colors.danger;

      return (
        <>
          <BarRounded
            key={`bar-${forecast}`}
            top
            radius={4}
            x={barX + offset}
            y={forecastY}
            width={barWidth}
            height={forecastHeight}
            fill={barForecastColor}
            opacity={0.6}
          />
          <BarRounded
            key={`bar-${value}`}
            top
            radius={0}
            x={barX + offset}
            y={barY}
            width={barWidth}
            height={barHeight}
            fill={barFillColor}
          />
        </>
      );
    }

    return (
      <BarRounded
        key={`bar-${value}`}
        top
        radius={2}
        x={barX + offset}
        y={barY}
        width={barWidth}
        height={barHeight}
        fill={barFillColor}
      />
    );
  });

  return <>{chartBars}</>;
}
